import { Button, Card, CardContent, CardHeader, CardTitle } from '@data-delivery/d3s';
import type { AssignmentEntity, PermissionApi } from '@data-delivery/permission-ui-api';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  useBusinessGroupPermission,
  WithBusinessGroupPermission,
} from '../business-group-permission-context';
import { WithPermission } from '../with-permission';

export interface AssignmentSummaryProps {
  assignment: AssignmentEntity;
  className?: React.HTMLAttributes<HTMLDivElement>['className'];
  userRoles?: PermissionApi.BusinessGroupRoleEntity[];
}

export function AssignmentSummary({
  assignment,
  className,
  userRoles = [],
}: AssignmentSummaryProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { businessGroupId } = useParams();
  const bgPermissionContext = useBusinessGroupPermission();
  const PermissionWrapper = bgPermissionContext ? WithBusinessGroupPermission : WithPermission;
  const field = (label: string, value: string | undefined) => (
    <div className='flex min-w-[140px] max-w-xs flex-col'>
      <span className='text-[10px] font-medium uppercase tracking-wide text-muted-foreground'>
        {label}
      </span>
      <span className='mt-1 break-words text-sm'>{value && value !== '' ? value : '—'}</span>
    </div>
  );
  return (
    <Card className={className}>
      <CardHeader className='pb-3'>
        <div className='flex items-start justify-between gap-2'>
          <CardTitle className='flex flex-col text-base'>
            <span>{assignment.name}</span>
            <span className='text-xs font-normal text-muted-foreground'>{assignment.id}</span>
          </CardTitle>
          {businessGroupId ? (
            <PermissionWrapper
              roles={userRoles}
              resource='assignments'
              action='update'
              data={assignment}
            >
              <Button
                size='sm'
                variant='secondary'
                onClick={() => {
                  navigate(
                    `/business-groups/${encodeURIComponent(
                      businessGroupId,
                    )}/assignments/${encodeURIComponent(assignment.id)}/edit`,
                    {
                      state: { backgroundLocation: location },
                    },
                  );
                }}
              >
                Edit
              </Button>
            </PermissionWrapper>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        <div className='flex flex-wrap gap-6'>
          {field('Description', assignment.description || undefined)}
          {field('Contract', assignment.contract || undefined)}
          {field(
            'Start Date',
            assignment.startDate ? new Date(assignment.startDate).toLocaleDateString() : undefined,
          )}
          {field(
            'End Date',
            assignment.endDate ? new Date(assignment.endDate).toLocaleDateString() : undefined,
          )}
          {field(
            'Last Modified',
            assignment.lastModified
              ? new Date(assignment.lastModified).toLocaleString()
              : undefined,
          )}
          {field(
            'Services',
            assignment.services?.length ? assignment.services.join(', ') : undefined,
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default AssignmentSummary;
